import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import DeepLinkCopiedModal from './DeepLinkCopiedModal';
import GivelifyBottomSheet from '../../common/Modal/GivelifyBottomSheet';
import type { Donee, SeedOfGoodResponse } from '../../../../../api';
import { useDonorProfile } from '../../../../../api';
import { usePushEvents } from '../../../../../hooks/PushEvents';
import {
    viewEvent,
    clickEvent,
    successEvent,
} from '../../../../../hooks/PushEvents/clevertapEvents';
import { useDeepLink } from '../../../../../hooks/useDeepLink';
import { isAppleDevice } from '../../common/utils';
import { appLinks } from '../../../../../utils/webConfig';
import { Text } from '../../common/Text/Text';
import { colors } from '../../../../theme';
import { SeedIconCircle } from '../../../../icons';
import { numberWithCommas } from '../../../../../utils/functions';
import { Button } from '../../../../ui';

const seedModalShownKey = 'seed-modal-shown';
const showSeedModal = !Boolean(window.localStorage.getItem(seedModalShownKey));
const setSeedModalShownLocal = () =>
    window.localStorage.setItem(seedModalShownKey, new Date().toISOString());

export const seedDeepLinkTitle = <>Get the free Givelify app</>;
export const seedDeepLinkSubTitle = (
    <>
        When you open the app, tap{' '}
        <span
            id="seed-deep-link-allow-paste-text"
            className="deep-link-copied-text-black"
        >
            “Allow Paste”
        </span>{' '}
        to sign in and claim your seeds
    </>
);
export const noSeedDeepLinkSubTitle = (
    <>
        When you open the app, tap{' '}
        <span
            id="no-seed-deep-link-allow-paste-text"
            className="deep-link-copied-text-black"
        >
            “Allow Paste”
        </span>{' '}
        to sign in and learn about seeds
    </>
);

const seedEventPayload = {
    name: 'ClaimSeedsOnApp',
    location: 'PointsBottomSheetPrompt',
    screen: 'ThankYouScreen',
    type: 'Button',
    action: 'clicked',
};
const noSeedEventPayload = {
    name: 'NoPointsGetApp',
    app: 'OGA',
    location: 'NoPointsBottomSheetPrompt',
    screen: 'ThankYouScreen',
    type: 'Button',
};
export const seedSuccessIosModalEventPayload = {
    name: 'SeedVoucherCopied',
    screen: 'ThankYouScreen',
    action: 'completed',
    voucher: 'DDL voucher',
};
export const seedClickIosModalEventPayload = {
    name: 'GoToAppStore',
    location: 'PointsClipboardPasteBottomSheet',
    screen: 'ThankYouScreen',
    type: 'Button',
    action: 'clicked',
};
export const noSeedClickIosModalEventPayload = {
    name: 'GoToAppStore',
    location: 'NoPointsClipboardPasteBottomSheet',
    screen: 'ThankYouScreen',
    type: 'Button',
    app: 'OGA',
};
export const seedViewIosModalEventPayload = {
    name: 'PointsClipboardPasteBottomSheet',
    location: 'SeedModal',
    screen: 'ThankYouScreen',
    action: 'shown',
};
export const noSeedViewIosModalEventPayload = {
    name: 'NpPointsClipboardPasteBottomSheet',
    location: 'ThankYouScreen',
    app: 'OGA',
};
export const seedCloseIosModalEventPayload = {
    eventName: clickEvent,
    eventPayload: {
        name: 'Dismiss',
        type: 'Button',
        location: 'PointsClipboardPasteBottomSheet',
        screen: 'ThankYouScreen',
        action: 'dismissed',
    },
};
export const noSeedCloseIosModalEventPayload = {
    eventName: clickEvent,
    eventPayload: {
        name: 'Dismiss',
        type: 'Button',
        location: 'NoPointsClipboardPasteBottomSheet',
        screen: 'ThankYouScreen',
        action: 'dismissed',
        app: 'OGA',
    },
};

interface SeedModalProps {
    donee: Donee;
    seedsBalance: number;
    seeds: SeedOfGoodResponse | undefined;
}

const StyledDiv = styled.div<{ showSeedModal: boolean }>`
    .seed-icon-circle-wrapper {
        text-align: center;
        margin-top: ${({ showSeedModal }) => (showSeedModal ? '24px' : '8px')};
        margin-bottom: 16px;
        position: relative;
        .new-badge {
            position: absolute;
            top: -10px;
            left: 50%;
            transform: translateX(-49%);
            font-size: 10px;
            padding: 3px 8px 2px 8px;
            border-radius: 16px;
            z-index: 9;
            line-height: 16px;
            letter-spacing: 0.25px;
            display: inline-block;
            color: ${colors.white};
            background-color: ${colors.primaryGuidingViolet};
        }
    }
    .seed-modal-heading {
        font-weight: 800;
        text-align: center;
    }
    .seed-modal-heading-line {
        display: block;
    }
    .seed-modal-text {
        text-align: center;
        max-width: 354px;
        font-size: 14px;
        line-height: 24px;
        letter-spacing: 0.25px;
        margin-top: 16px;
        margin-bottom: 16px;
        color: ${colors.primarySecondaryContent};
    }
`;

const SeedModal: React.FC<SeedModalProps> = ({
    donee,
    seedsBalance,
    seeds,
}) => {
    const { pushEvent } = usePushEvents();
    const { donorProfile } = useDonorProfile();
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [show, setShow] = useState<boolean>(false);
    const handleDownloadClick = useCallback(() => {
        window.location.href = isAppleDevice()
            ? appLinks.seedModal.apple
            : appLinks.seedModal.android;
        setShow(false);
    }, []);
    const { deepLinkState, getDeepLink, showCopiedModal, setShowCopiedModal } =
        useDeepLink(
            clickEvent,
            seedsBalance > 0 ? seedEventPayload : noSeedEventPayload,
            handleDownloadClick,
        );

    const title = useMemo(() => {
        if (seedsBalance > 0) {
            return (
                <>
                    <span
                        id="seed-modal-title-balance"
                        className="seed-modal-heading-line"
                    >
                        {numberWithCommas(seedsBalance)} Seeds of Good
                    </span>
                    <span
                        id="seed-modal-title-description"
                        className="seed-modal-heading-line"
                    >
                        created by your gifts
                    </span>
                </>
            );
        }
        return (
            <>
                <span
                    id="seed-modal-no-seeds-title-introduction"
                    className="seed-modal-heading-line"
                >
                    Give in the app to start earning
                </span>
                <span
                    id="seed-modal-no-seeds-title-program"
                    className="seed-modal-heading-line"
                >
                    Seeds of Good!
                </span>
            </>
        );
    }, [seedsBalance]);

    const subTitle = useMemo(() => {
        if (seedsBalance > 0) {
            return 'Seeds are like points, claim them on the app to send a free bonus donation';
        }
        return 'Seeds are like points that turn into free bonus donations for your favorite organization';
    }, [seedsBalance]);

    const ctaText = useMemo(() => {
        if (seedsBalance > 0) {
            return 'Get App to Claim Seeds';
        }
        return 'Get the Givelify App';
    }, [seedsBalance]);

    const handleGetClick = useCallback(() => {
        getDeepLink(donee, donorProfile, '/dl/points', {
            key: 'points',
            data: seeds || {},
        }).then((s) => {
            s && setUrl(s);
        });
    }, [getDeepLink, donee, donorProfile, seeds]);

    const handleClose = useCallback(() => {
        setShow(false);
        pushEvent(clickEvent, {
            name: 'Dismiss',
            location:
                seedsBalance > 0
                    ? 'PointsBottomSheetPrompt'
                    : 'NoPointsBottomSheetPrompt',
            screen: 'ThankYouScreen',
            type: 'Button',
            app: 'OGA',
        });
    }, [pushEvent, seedsBalance]);

    useEffect(() => {
        window.setTimeout(() => {
            setShow(true);
            setSeedModalShownLocal();
            pushEvent(viewEvent, {
                name:
                    seedsBalance > 0
                        ? 'PointsBottomSheetPrompt'
                        : 'NoPointsBottomSheetPrompt',
                location: 'ThankYouScreen',
                action: 'shown',
                app: 'OGA',
            });
        }, 1200);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!show) return null;

    return (
        <>
            <GivelifyBottomSheet
                id="seed-modal"
                onClosedCallback={handleClose}
                initialOpenValue={window.innerWidth < 501}
                contentPadding="12px 24px 32px 24px"
            >
                <StyledDiv
                    id="seed-modal-body"
                    showSeedModal={showSeedModal}
                >
                    <div
                        id="seed-modal-icon-container"
                        className="seed-icon-circle-wrapper"
                    >
                        {showSeedModal && (
                            <div
                                id="seed-modal-new-badge"
                                className="new-badge"
                            >
                                NEW
                            </div>
                        )}
                        <SeedIconCircle
                            id="seed-modal-icon"
                            className="seed-icon-circle"
                        />
                    </div>
                    <Text
                        id="seed-modal-title"
                        variant="large"
                        className="seed-modal-heading"
                    >
                        {title}
                    </Text>
                    <Text
                        id="seed-modal-description"
                        className="seed-modal-text"
                    >
                        {subTitle}
                    </Text>
                    <Button
                        id="seed-modal-get-app-button"
                        loading={deepLinkState.loading}
                        onClick={handleGetClick}
                        className="w-full"
                    >
                        {ctaText}
                    </Button>
                </StyledDiv>
            </GivelifyBottomSheet>
            <DeepLinkCopiedModal
                id="seed-modal-deep-link-modal"
                url={url}
                open={showCopiedModal}
                title={seedDeepLinkTitle}
                subTitle={
                    seedsBalance > 0
                        ? seedDeepLinkSubTitle
                        : noSeedDeepLinkSubTitle
                }
                viewEvent={viewEvent}
                clickEvent={clickEvent}
                successEvent={successEvent}
                viewEventPayload={
                    seedsBalance > 0
                        ? seedViewIosModalEventPayload
                        : noSeedViewIosModalEventPayload
                }
                clickEventPayload={
                    seedsBalance > 0
                        ? seedClickIosModalEventPayload
                        : noSeedClickIosModalEventPayload
                }
                successEventPayload={seedSuccessIosModalEventPayload}
                closeEventPayload={
                    seedsBalance > 0
                        ? seedCloseIosModalEventPayload
                        : noSeedCloseIosModalEventPayload
                }
                onClick={handleDownloadClick}
                onClose={() => setShowCopiedModal(false)}
            />
        </>
    );
};

export default SeedModal;
